import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';
import bridgeService from '../../../services/bridgeService';
import Loading from '../../../components/common/Loading';
import AppSelect from '../../../components/common/AppSelect';
import useNotify from '../../../hooks/useNotify';
import { filterBridgesByRole } from '../../../utils/bridgeAccess';
import BridgeDashboardView from '../components/BridgeDashboardView';
import './DashboardPage.css';

export default function DashboardPage() {
  const { user } = useAuth();

  const [bridges, setBridges] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showToast } = useNotify();

  const loadBridgeDetail = async (id, listFallback) => {
    try {
      return await bridgeService.fetchDetail(id);
    } catch {
      return listFallback?.find((b) => b._id === id) || null;
    }
  };

  const load = useCallback(async (bridgeId) => {
    setLoading(true);
    try {
      const data = await bridgeService.getAll();
      const list = filterBridgesByRole(data.bridges || [], user);
      setBridges(list);
      const targetId = bridgeId || list[0]?._id;
      if (targetId) {
        // getAll already returns populated staff/sensors — avoid a second detail call
        const fromList = list.find((b) => String(b._id) === String(targetId));
        if (fromList) {
          setSelected(fromList);
        } else {
          const full = await loadBridgeDetail(targetId, list);
          if (full) setSelected(full);
        }
      } else {
        setSelected(null);
      }
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, user]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Loading />;
  if (!bridges.length) {
    return (
      <div className="card" style={{ padding: 20, color: '#888' }}>
        To manage and add bridge details, navigate to Assets from the main menu.
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-bridge-toolbar">
        <label>Select Bridge:</label>
        <AppSelect
          value={selected?._id || ''}
          placeholder="Select Bridge"
          onChange={async (id) => {
            const full = await loadBridgeDetail(id, bridges);
            if (full) setSelected(full);
          }}
          options={bridges.map((b) => ({ value: b._id, label: b.name }))}
          minWidth={220}
        />
      </div>

      {selected && (
        <BridgeDashboardView
          selected={selected}
          onRefresh={load}
        />
      )}
    </div>
  );
}
